import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    EliminationBalanceSheet, EliminationIncomeStatement, EliminationLine, Money,
} from '../../interfaces/interSegment.interface';
import { InterSegmentService } from '../../services/interSegment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';

const yearStart = (): string => `${new Date().getFullYear()}-01-01`;

const Section: React.FC<{ title: string; lines: EliminationLine[]; totals: [Money, Money, Money] }> = ({ title, lines, totals }) => (
    <>
        <TableRow><TableCell colSpan={4} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>{title}</TableCell></TableRow>
        {lines.map(l => (
            <TableRow key={l.accountId}>
                <TableCell>{l.accountCode} - {l.accountName}</TableCell>
                <TableCell align="right">{formatMoney(l.gross)}</TableCell>
                <TableCell align="right">{Number(l.elimination) === 0 ? '' : `(${formatMoney(l.elimination)})`}</TableCell>
                <TableCell align="right">{formatMoney(l.net)}</TableCell>
            </TableRow>
        ))}
        <TableRow>
            <TableCell><b>Total {title.toLowerCase()}</b></TableCell>
            <TableCell align="right"><b>{formatMoney(totals[0])}</b></TableCell>
            <TableCell align="right"><b>({formatMoney(totals[1])})</b></TableCell>
            <TableCell align="right"><b>{formatMoney(totals[2])}</b></TableCell>
        </TableRow>
    </>
);

/**
 * "All segments (eliminated)": the sum of the per-segment statements (gross), less every line that carries a
 * counterparty segment (elimination, intra-entity by definition), equals the net company view.
 */
const InterSegmentElimination: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [tab, setTab] = useState(0);
    const [asOf, setAsOf] = useState(toLocalIsoDate());
    const [from, setFrom] = useState(yearStart());
    const [to, setTo] = useState(toLocalIsoDate());
    const [bs, setBs] = useState<EliminationBalanceSheet | null>(null);
    const [is, setIs] = useState<EliminationIncomeStatement | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            if (tab === 0) setBs(await InterSegmentService.eliminationBalanceSheet(institutionId, asOf));
            else setIs(await InterSegmentService.eliminationIncomeStatement(institutionId, from, to));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the statement');
        } finally {
            setLoading(false);
        }
    }, [institutionId, tab, asOf, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const header = (
        <TableHead>
            <TableRow>
                <TableCell data-help="accounting.interSegElim.col.account">Account</TableCell><TableCell data-help="accounting.interSegElim.col.gross" align="right">Gross (all segments)</TableCell>
                <TableCell data-help="accounting.interSegElim.col.elimination" align="right">Inter-segment elimination</TableCell><TableCell data-help="accounting.interSegElim.col.net" align="right">Net</TableCell>
            </TableRow>
        </TableHead>
    );

    return (
        <Box data-help-page="accounting.interSegElim.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">Inter-segment eliminations</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Button data-help="accounting.interSegElim.balanceSheetLink" component={RouterLink} to="/accounting/balance-sheet" variant="outlined">Segment balance sheet</Button>
                    <Button data-help="accounting.interSegElim.reconLink" component={RouterLink} to="/accounting/inter-segment/reconciliation" variant="outlined">Reconciliation</Button>
                </Box>
            </Box>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab data-help="accounting.interSegElim.tabBalanceSheet" label="Balance sheet" /><Tab data-help="accounting.interSegElim.tabIncome" label="Income statement" />
            </Tabs>
            <Box data-help-section="accounting.interSegElim.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                {tab === 0 ? (
                    <TextField data-help="accounting.interSegElim.asOf" label="As of" type="date" size="small" value={asOf} InputLabelProps={{ shrink: true }}
                        onChange={e => setAsOf(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                ) : (
                    <>
                        <TextField data-help="accounting.interSegElim.from" label="From" type="date" size="small" value={from} InputLabelProps={{ shrink: true }}
                            onChange={e => setFrom(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                        <TextField data-help="accounting.interSegElim.to" label="To" type="date" size="small" value={to} InputLabelProps={{ shrink: true }}
                            onChange={e => setTo(e.target.value)} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                    </>
                )}
                {tab === 0 && bs && (
                    <Chip color={bs.balanced ? 'success' : 'error'} label={bs.balanced ? 'Balanced' : 'Does not balance'} />
                )}
            </Box>
            {tab === 0 && bs && !bs.balanced && (
                <Alert severity="warning">
                    The eliminated sheet does not balance: some due-from / due-to lines have no counterparty or the pair
                    disagrees. See the reconciliation matrix.
                </Alert>
            )}
            {loading && <Typography color="text.secondary">Loading…</Typography>}

            {tab === 0 && bs && (
                <TableContainer component={Paper}>
                    <Table data-help-section="accounting.interSegElim.bsTable" size="small">
                        {header}
                        <TableBody>
                            <Section title="Assets" lines={bs.assets}
                                totals={[bs.grossTotalAssets, bs.eliminationAssets, bs.netTotalAssets]} />
                            <Section title="Liabilities" lines={bs.liabilities}
                                totals={[
                                    bs.liabilities.reduce((a, l) => a + Number(l.gross), 0),
                                    bs.liabilities.reduce((a, l) => a + Number(l.elimination), 0),
                                    bs.liabilities.reduce((a, l) => a + Number(l.net), 0),
                                ]} />
                            <Section title="Equity" lines={bs.equity}
                                totals={[
                                    bs.equity.reduce((a, l) => a + Number(l.gross), 0),
                                    bs.equity.reduce((a, l) => a + Number(l.elimination), 0),
                                    bs.equity.reduce((a, l) => a + Number(l.net), 0),
                                ]} />
                            <TableRow>
                                <TableCell><i>Current earnings (all segments)</i></TableCell>
                                <TableCell align="right">{formatMoney(bs.currentEarnings)}</TableCell><TableCell /><TableCell align="right">{formatMoney(bs.currentEarnings)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell><b>Total liabilities and equity</b></TableCell>
                                <TableCell align="right"><b>{formatMoney(bs.grossTotalLiabilitiesAndEquity)}</b></TableCell>
                                <TableCell align="right"><b>({formatMoney(bs.eliminationLiabilitiesAndEquity)})</b></TableCell>
                                <TableCell align="right"><b>{formatMoney(bs.netTotalLiabilitiesAndEquity)}</b></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {tab === 1 && is && (
                <TableContainer component={Paper}>
                    <Table data-help-section="accounting.interSegElim.isTable" size="small">
                        {header}
                        <TableBody>
                            <Section title="Revenue" lines={is.revenue} totals={[is.grossRevenue, is.eliminationRevenue, is.netRevenue]} />
                            <Section title="Expenses" lines={is.expenses} totals={[is.grossExpense, is.eliminationExpense, is.netExpense]} />
                            <TableRow>
                                <TableCell><b>Profit</b></TableCell>
                                <TableCell align="right"><b>{formatMoney(is.grossProfit)}</b></TableCell><TableCell />
                                <TableCell align="right"><b>{formatMoney(is.netProfit)}</b></TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default InterSegmentElimination;
